import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import { usePrevious } from '_hooks';

import Button from 'components/shared/Button';
import Spinner from 'components/shared/Spinner';
import BaseField from 'components/shared/fields/BaseField';
import DateField from 'components/shared/fields/DateField';
import RadioGroupField from 'components/shared/fields/RadioGroupField';
import Text from 'components/shared/layouts/Text';

import { convertNumericValues } from '../helpers';

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
  min-height: 42px;
`;

const PhysicalInfoForm = ({ loading, error, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const prevDefaultValues = usePrevious(defaultValues);
  const values = useWatch({ control });

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (!isEqual(prevDefaultValues, defaultValues)) {
      reset(defaultValues);
    }
  }, [reset, prevDefaultValues, defaultValues]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12} md={6}>
          <Text
            size={theme.fontSizes.small}
            lineHeight={theme.fontSizes.small}
            weight={500}
            color={theme.colors.gray.secondary}
            bottomSpace={theme.spaces.regular}
          >
            Gender
          </Text>
          <RadioGroupField {...getFieldProps(FIELD_KEYS.gender)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <DateField {...getFieldProps(FIELD_KEYS.dateOfBirth)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.heightFt)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.heightIn)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.weightLbs)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField
            {...getFieldProps(FIELD_KEYS.desiredWeightLbs)}
            control={control}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={12} md={4}>
          <Button
            type="submit"
            disabled={isEqual(defaultValues, convertNumericValues(values)) || submitDisabled}
            color={theme.colors.cyanBlue}
          >
            Save
          </Button>
        </Col>
        <Col xs={12} md={8}>
          {loading && (
            <Text
              size={theme.fontSizes.small}
              color={theme.colors.gray.secondary}
              topSpace={isMobile ? theme.spaces.regular : undefined}
            >
              <StyledMessageContainer>
                <Spinner />
                <div>Updating...</div>
              </StyledMessageContainer>
            </Text>
          )}
        </Col>
      </Row>

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </Form>
  );
};

PhysicalInfoForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default PhysicalInfoForm;
